/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/lib/assist/grammar.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)   | Who               | Why                       | What Changed          |
 * |---|---------------------|-------------------|---------------------------|------------------------|
 * | 1 | 2026-07-06          | Claude Sonnet 5   | AI Assist port (from Plex)| Created — Lane 0       |
 * |   |                     |                   |                           | deterministic grammar  |
 * |   |                     |                   |                           | parser (zero AI),      |
 * |   |                     |                   |                           | Android role/key lexicon|
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/lib/assist/buildStep.ts
 * - apps/studio/src/components/AssistBar.tsx
 * - apps/studio/src/lib/assist/__tests__/grammar.test.ts
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - parseAssistSentence — sentence (string) -> ParseResult (intent or suggestions)
 * - GRAMMAR_EXAMPLES    — rotating ghost-text examples for the command bar
 * - AssistIntent / AssistIntentKind / ParseResult — types
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Ported from D:\Plex\apps\studio-web\src\lib\assist\grammar.ts (Lane 0 of the
 *    AI Assist design). Zero AI, zero tokens — a deterministic verb-lexicon
 *    grammar, same spirit as testRigor's "plain English is a forgiving DSL".
 * 2. Every supported sentence family maps 1:1 onto a real @mimiq/ir IRStep type
 *    (see packages/ir/src/schemas/steps.ts) — nothing here is aspirational.
 *    Dropped vs. Plex's web grammar: launchApp/stopApp (no NL package-name/picker
 *    flow exists yet — reachable via the Custom Commands panel instead),
 *    coordinate-only swipe (no element target to resolve against). Added vs.
 *    Plex: pressKey "back"/"home" (real Android hardware/nav keys with no DOM
 *    equivalent).
 * 3. ROLE_NOUNS maps spoken nouns to ANDROID CLASSNAME SUBSTRINGS, not ARIA
 *    roles — the uiautomator tree has no "role" concept, only `class` (e.g.
 *    "android.widget.Button") plus boolean flags. Matching is `className
 *    .includes(filter)`, kept as a HINT exactly like Plex (resolver falls back
 *    to the unfiltered pool on a total miss within the role-filtered pool).
 * 4. Quoted text ("..." or '...') is always treated as literal — this is how
 *    the grammar disambiguates the target/value from surrounding sentence
 *    noise. Unquoted targets are still accepted for the common bare
 *    click/assert case to keep the DSL forgiving.
 * 5. Failure mode is NEVER a bare error: parseAssistSentence returns
 *    `{ ok:false, suggestions }` with the 3 closest supported patterns so the
 *    UI can show a "did you mean" hint instead of a dead end.
 * =============================================================================
 */

export type AssistIntentKind =
  | "openUrl"
  | "tap"
  | "doubleTap"
  | "longPress"
  | "typeText"
  | "clearText"
  | "pressKey"
  | "assertVisible"
  | "assertNotVisible"
  | "assertEnabled"
  | "assertDisabled"
  | "assertPageContainsText"
  | "assertHasText"
  | "wait"
  | "waitForVisible"
  | "scrollUntilVisible"
  | "screenshot";

export interface AssistIntent {
  kind: AssistIntentKind;
  raw: string;
  /** openUrl */
  url?: string;
  /** typeText / assertHasText / assertPageContainsText */
  text?: string;
  /** tap/doubleTap/longPress/clearText/waitForVisible/scrollUntilVisible/assertVisible/.../assertHasText target phrase */
  target?: string;
  /** Android className-substring filter derived from a role noun in the sentence (e.g. "button" -> "Button") */
  role?: string;
  /** pressKey */
  key?: "back" | "home" | "enter" | "tab" | "delete";
  /** wait (seconds -> caller multiplies by 1000 for durationMs) */
  seconds?: number;
}

export type ParseResult =
  | { ok: true; intent: AssistIntent }
  | { ok: false; suggestions: string[] };

/** Rotating ghost-text examples shown in the command bar placeholder. */
export const GRAMMAR_EXAMPLES: string[] = [
  "click on the button called \"Sign in\"",
  "type \"jeyaprakash32@gmail.com\" into the Email field",
  "verify that the page contains \"Welcome back\"",
  "wait until the button \"Continue\" is visible",
  "clear the Search field",
  "press the back key",
  "wait 2 seconds",
  "scroll until \"Add to cart\" is visible",
  "take a screenshot",
  "goto https://example.com",
];

// ─── Role noun lexicon — maps a spoken role word to an Android className substring ──

const ROLE_NOUNS: Record<string, string> = {
  button: "Button",
  field: "EditText",
  textbox: "EditText",
  input: "EditText",
  "text field": "EditText",
  "search box": "EditText",
  searchbox: "EditText",
  checkbox: "CheckBox",
  radio: "RadioButton",
  "radio button": "RadioButton",
  switch: "Switch",
  toggle: "Switch",
  tab: "Tab",
  dropdown: "Spinner",
  spinner: "Spinner",
  combobox: "Spinner",
  menu: "Menu",
  "menu item": "MenuItem",
  icon: "ImageView",
  image: "ImageView",
  heading: "TextView",
  label: "TextView",
  text: "TextView",
};

// Sorted longest-first so multi-word nouns ("radio button") match before
// their single-word substrings ("button").
const ROLE_NOUN_KEYS = Object.keys(ROLE_NOUNS).sort((a, b) => b.length - a.length);

/**
 * Strips a role noun off a target phrase, returning { role, rest }.
 * Checks PREFIX form first ("button called X", "the checkbox agree to terms")
 * then SUFFIX form ("the Email field", "the Country dropdown") — real-world
 * phrasing uses both, and prefix wins when a phrase could parse either way.
 */
function extractRole(phrase: string): { role?: string; rest: string } {
  const trimmed = phrase.trim();

  for (const noun of ROLE_NOUN_KEYS) {
    const re = new RegExp(`^${escapeRegExp(noun)}\\b\\s*`, "i");
    if (re.test(trimmed)) {
      return { role: ROLE_NOUNS[noun], rest: trimmed.replace(re, "").trim() };
    }
  }

  for (const noun of ROLE_NOUN_KEYS) {
    const re = new RegExp(`\\s+${escapeRegExp(noun)}$`, "i");
    if (re.test(trimmed)) {
      return { role: ROLE_NOUNS[noun], rest: trimmed.replace(re, "").trim() };
    }
  }

  return { rest: trimmed };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Extracts a quoted string ("..." or '...') anywhere in the text, or falls back to the raw text. */
function extractQuotedOrRaw(text: string): string {
  const m = text.match(/"([^"]+)"|'([^']+)'/);
  if (m) return (m[1] ?? m[2] ?? "").trim();
  return text.trim();
}

/** Strips "called|named|labeled|with text|saying" connector before a target phrase. */
function stripTargetConnectors(phrase: string): string {
  return phrase
    .replace(/^(?:called|named|labell?ed|with text|saying)\s+/i, "")
    .replace(/^the\s+/i, "")
    .trim();
}

/** Cleans a target phrase end-to-end: role extraction + connector stripping + quote extraction. */
function parseTargetPhrase(raw: string): { role?: string; target: string } {
  let phrase = raw.trim().replace(/^the\s+/i, "");
  const { role, rest } = extractRole(phrase);
  phrase = stripTargetConnectors(rest);
  const target = extractQuotedOrRaw(phrase);
  return { role, target };
}

const KEY_NAMES: Record<string, AssistIntent["key"]> = {
  back: "back",
  home: "home",
  enter: "enter",
  return: "enter",
  tab: "tab",
  delete: "delete",
  del: "delete",
};

// ─── Sentence family matchers — checked in priority order ───────────────────

interface Matcher {
  pattern: RegExp;
  build: (m: RegExpMatchArray, raw: string) => AssistIntent | null;
}

const URL_LIKE = /^(?:https?:\/\/|www\.)/i;
const BARE_DOMAIN = /^[\w-]+(\.[\w-]+)+([/?#]\S*)?$/i;

function normalizeUrl(fragment: string): string {
  const stripped = fragment.trim().replace(/^["']|["']$/g, "");
  if (URL_LIKE.test(stripped)) return stripped.startsWith("http") ? stripped : `https://${stripped}`;
  if (BARE_DOMAIN.test(stripped)) return `https://${stripped}`;
  return stripped;
}

const MATCHERS: Matcher[] = [
  // ── Navigation ──────────────────────────────────────────────────────────
  {
    pattern: /^(?:please\s+)?(?:go\s*to|goto|open|navigate\s*to|visit)\s+(.+)$/i,
    build: (m, raw) => ({ kind: "openUrl", raw, url: normalizeUrl(extractQuotedOrRaw(m[1]!)) }),
  },

  // ── Press key (checked before generic click so "press back" resolves to
  //    a key event, not a tap on an element literally named "back") ────────
  {
    pattern: /^(?:please\s+)?press\s+(?:the\s+)?([a-z]+)(?:\s+key)?$/i,
    build: (m, raw) => {
      const key = KEY_NAMES[m[1]!.toLowerCase()];
      return key ? { kind: "pressKey", raw, key } : null;
    },
  },

  // ── Double tap / long press / tap-family ─────────────────────────────────
  {
    pattern: /^(?:please\s+)?(double[\s-]?tap|double[\s-]?click)\s+(?:on\s+)?(.+)$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[2]!);
      return { kind: "doubleTap", raw, role, target };
    },
  },
  {
    pattern: /^(?:please\s+)?(long[\s-]?press)\s+(?:on\s+)?(.+)$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[2]!);
      return { kind: "longPress", raw, role, target };
    },
  },
  {
    pattern: /^(?:please\s+)?(click|tap|press|hit)\s+(?:on\s+)?(.+)$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[2]!);
      return { kind: "tap", raw, role, target };
    },
  },

  // ── Type / clear ──────────────────────────────────────────────────────────
  {
    pattern: /^(?:please\s+)?(?:type|enter|fill(?:\s+in)?|input)\s+"([^"]+)"\s*(?:into|in|on)?\s*(.*)$/i,
    build: (m, raw) => {
      const text = m[1]!;
      const targetRaw = (m[2] ?? "").trim();
      if (!targetRaw) return { kind: "typeText", raw, text };
      const { role, target } = parseTargetPhrase(targetRaw);
      return { kind: "typeText", raw, text, role, target };
    },
  },
  {
    pattern: /^(?:please\s+)?clear\s+(?:the\s+)?(.+)$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[1]!);
      return { kind: "clearText", raw, role, target };
    },
  },

  // ── Assertions ──────────────────────────────────────────────────────────
  {
    pattern: /^(?:please\s+)?(?:verify|check|assert)\s+(?:that\s+)?(?:the\s+)?page\s+contains\s+"([^"]+)"$/i,
    build: (m, raw) => ({ kind: "assertPageContainsText", raw, text: m[1]! }),
  },
  {
    pattern: /^(?:please\s+)?(?:verify|check|assert)\s+(?:that\s+)?(?:the\s+)?(.+?)\s+(?:has\s+text|contains)\s+"([^"]+)"$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[1]!);
      return { kind: "assertHasText", raw, role, target, text: m[2]! };
    },
  },
  {
    pattern: /^(?:please\s+)?(?:verify|check|assert)\s+(?:that\s+)?(?:the\s+)?(.+?)\s+is\s+(visible|hidden|not\s+visible|enabled|disabled)$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[1]!);
      const state = m[2]!.toLowerCase().replace(/\s+/g, " ");
      const kind: AssistIntentKind =
        state === "visible" ? "assertVisible"
        : state === "hidden" || state === "not visible" ? "assertNotVisible"
        : state === "enabled" ? "assertEnabled"
        : "assertDisabled";
      return { kind, raw, role, target };
    },
  },

  // ── Wait ──────────────────────────────────────────────────────────────────
  {
    pattern: /^(?:please\s+)?wait\s+(?:until|for)\s+(?:the\s+)?(.+?)\s+(?:is\s+|to\s+be\s+)?visible$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[1]!);
      return { kind: "waitForVisible", raw, role, target };
    },
  },
  {
    pattern: /^(?:please\s+)?wait\s+(\d+(?:\.\d+)?)\s*(?:s|sec|secs|second|seconds)?$/i,
    build: (m, raw) => ({ kind: "wait", raw, seconds: parseFloat(m[1]!) }),
  },

  // ── Scroll (targeted only — IR has no directionless scroll step) ─────────
  {
    pattern: /^(?:please\s+)?scroll\s+(?:until|to)\s+(?:the\s+)?(.+?)\s+(?:is\s+)?visible$/i,
    build: (m, raw) => {
      const { role, target } = parseTargetPhrase(m[1]!);
      return { kind: "scrollUntilVisible", raw, role, target };
    },
  },

  // ── Screenshot ────────────────────────────────────────────────────────────
  {
    pattern: /^(?:please\s+)?(?:take\s+)?(?:a\s+)?screenshot$/i,
    build: (_m, raw) => ({ kind: "screenshot", raw }),
  },
];

const SUGGESTION_POOL = GRAMMAR_EXAMPLES;

/** Returns the 3 example patterns most similar (by shared word count) to the failed input. */
function closestSuggestions(sentence: string): string[] {
  const words = new Set(sentence.toLowerCase().split(/\s+/).filter(Boolean));
  const scored = SUGGESTION_POOL.map((example) => {
    const exampleWords = example.toLowerCase().split(/\s+/);
    const overlap = exampleWords.filter((w) => words.has(w)).length;
    return { example, overlap };
  });
  scored.sort((a, b) => b.overlap - a.overlap);
  return scored.slice(0, 3).map((s) => s.example);
}

/**
 * Parses a plain-English AI Assist sentence into a structured intent.
 * Zero AI — a deterministic grammar. Never throws; on failure returns
 * `{ ok:false, suggestions }` with the 3 closest supported patterns.
 */
export function parseAssistSentence(sentence: string): ParseResult {
  const trimmed = sentence.trim().replace(/\s+/g, " ").replace(/\.$/, "");
  if (!trimmed) return { ok: false, suggestions: SUGGESTION_POOL.slice(0, 3) };

  for (const matcher of MATCHERS) {
    const m = trimmed.match(matcher.pattern);
    if (!m) continue;
    const intent = matcher.build(m, trimmed);
    if (intent) return { ok: true, intent };
  }

  return { ok: false, suggestions: closestSuggestions(trimmed) };
}
